import React, { useEffect, useState } from 'react';

import { Button, Divider, Group, Paper, Stack, Text, Title, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconEdit, IconPlus, IconTrashX } from '@tabler/icons-react';
import Menu from 'components/Menu';
import AthleteStatusBadge from 'components/status-badges/AthleteStatusBadge';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useAthleteStatuses from 'hooks/athleteStatus/useAthleteStatuses';
import { CellProps } from 'react-table';
import { AthleteRetrieve, AthleteStatus } from 'types';

import CreateStatusModal from './components/CreateStatusModal';
import DeleteStatusModal from './components/DeleteStatusModal';
import EditStatusModal from './components/EditStatusModal';

interface IStatusHistoryCardProps {
  athlete: AthleteRetrieve;
  permission?: boolean;
}

const StatusHistoryCard = ({ athlete, permission = true }: IStatusHistoryCardProps): JSX.Element => {
  const theme = useMantineTheme();
  const { dateFormat } = useLocalization();

  const [isCreateStatusModalOpen, { open: openCreateStatusModal, close: closeCreateStatusModal }] =
    useDisclosure(false);
  const [isEditStatusModalOpen, { open: openEditStatusModal, close: closeEditStatusModal }] =
    useDisclosure(false);
  const [isDeleteStatusModalOpened, { open: openDeleteStatusModal, close: closeDeleteStatusModal }] =
    useDisclosure(false);

  const [athleteStatusTableData, setAthleteStatusTableData] = useState<AthleteStatus[]>([]);
  const [selectedAthleteStatus, setSelectedAthleteStatus] = useState<AthleteStatus | null>(null);
  const [previousStatus, setPreviousStatus] = useState<AthleteStatus | null>(null);
  const [followingStatus, setFollowingStatus] = useState<AthleteStatus | null>(null);

  const {
    data: athleteStatusData,
    isSuccess: athleteStatusIsSuccess,
    isError: athleteStatusIsError,
    isLoading: athleteStatusIsLoading,
  } = useAthleteStatuses({
    athlete: athlete?.athleteId,
  });

  const handleSelectStatus = (
    athleteStatus: AthleteStatus | null,
    previous: AthleteStatus | null,
    following: AthleteStatus | null,
  ): void => {
    setSelectedAthleteStatus(athleteStatus);
    setPreviousStatus(previous);
    setFollowingStatus(following);
  };

  const handleOpen = (
    athleteStatus: AthleteStatus | null,
    previous: AthleteStatus | null,
    following: AthleteStatus | null,
    openFunction: () => void,
  ): void => {
    handleSelectStatus(athleteStatus, previous, following);
    openFunction();
  };

  const handleClose = (closeFunction: () => void): void => {
    closeFunction();
    setSelectedAthleteStatus(null);
    setPreviousStatus(null);
    setFollowingStatus(null);
  };

  const columns = [
    {
      Header: 'Status Duration',
      Cell: ({ row }: CellProps<AthleteStatus>) => (
        <Group>
          <Text>{`${dayjs(row.original.startDate).format(dateFormat)} - ${
            row.original.endDate ? dayjs(row.original.endDate).format(dateFormat) : 'Ongoing'
          }`}</Text>
        </Group>
      ),
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }: CellProps<AthleteStatus>) => <AthleteStatusBadge status={value} />,
    },
    {
      id: 'actions',
      Cell: ({ row }: CellProps<AthleteStatus>) => (
        <Group position='right'>
          <Menu
            data-cy='statusRowButton'
            disabled={!permission}
            items={[
              {
                title: 'Edit',
                icon: <IconEdit size={theme.fontSizes.sm} />,
                onClick: () =>
                  handleOpen(
                    row.original,
                    athleteStatusTableData[row.index + 1] || null,
                    athleteStatusTableData[row.index - 1] || null,
                    openEditStatusModal,
                  ),
                'data-cy': 'statusEditButton',
              },
              {
                title: 'Delete',
                icon: <IconTrashX size={theme.fontSizes.sm} />,
                color: 'red',
                disabled: athleteStatusTableData.length === 1,
                onClick: () =>
                  handleOpen(
                    row.original,
                    athleteStatusTableData[row.index + 1] || null,
                    athleteStatusTableData[row.index - 1] || null,
                    openDeleteStatusModal,
                  ),
                'data-cy': 'statusDeleteButton',
              },
            ]}
          />
        </Group>
      ),
    },
  ];

  useEffect(() => {
    if (athleteStatusIsSuccess && athleteStatusData.data) {
      setAthleteStatusTableData(athleteStatusData.data);
    }
  }, [athleteStatusIsSuccess, athleteStatusData]);

  return (
    <>
      <Paper shadow='xl' radius='md' withBorder py='sm' px={0}>
        <Stack spacing={0}>
          <Group position='apart' px='md' pb='sm'>
            <Title order={4}>Status History</Title>
            <Button
              data-cy='addStatusButton'
              compact
              leftIcon={<IconPlus size={theme.fontSizes.md} />}
              onClick={() =>
                handleOpen(
                  null,
                  athleteStatusTableData.length > 0 ? athleteStatusTableData[0] : null,
                  null,
                  openCreateStatusModal,
                )
              }
              disabled={!permission || !athleteStatusIsSuccess}
            >
              Add Status
            </Button>
          </Group>
          <Divider />
          <Table
            tableData={athleteStatusTableData}
            columns={columns}
            isSuccess={athleteStatusIsSuccess}
            isLoading={athleteStatusIsLoading}
            isError={athleteStatusIsError}
            margin={8}
          />
        </Stack>
      </Paper>
      <CreateStatusModal
        opened={isCreateStatusModalOpen}
        athlete={athlete}
        previousStatus={previousStatus}
        closeHandler={() => handleClose(closeCreateStatusModal)}
      />
      <EditStatusModal
        opened={isEditStatusModalOpen}
        athlete={athlete}
        athleteStatus={selectedAthleteStatus}
        previousStatus={previousStatus}
        followingStatus={followingStatus}
        closeHandler={() => handleClose(closeEditStatusModal)}
      />
      <DeleteStatusModal
        opened={isDeleteStatusModalOpened}
        athlete={athlete}
        athleteStatus={selectedAthleteStatus}
        previousStatus={previousStatus}
        followingStatus={followingStatus}
        closeHandler={() => handleClose(closeDeleteStatusModal)}
      />
    </>
  );
};

export default StatusHistoryCard;
